export function normalizeMediaTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/**
 * Expands a single tag value that may be a PostgreSQL text[] literal (e.g. `{a,b}` or
 * `{"hr office",running}`) stored incorrectly as one array element.
 */
export function expandMediaAssetTag(raw: string): string[] {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '{}') return [];

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    const inner = trimmed.slice(1, -1).trim();
    if (!inner) return [];

    const parts: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let index = 0; index < inner.length; index += 1) {
      const character = inner[index];
      if (character === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (character === ',' && !inQuotes) {
        const next = normalizeMediaTag(current);
        if (next) parts.push(next);
        current = '';
        continue;
      }
      current += character;
    }

    const last = normalizeMediaTag(current);
    if (last) parts.push(last);
    return parts;
  }

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.flatMap((item) => expandMediaAssetTag(String(item)));
      }
    } catch {
      // Fall through to treat as a plain tag.
    }
  }

  const normalized = normalizeMediaTag(trimmed);
  return normalized ? [normalized] : [];
}

export function normalizeMediaTags(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const tag of tags) {
    const next = normalizeMediaTag(String(tag));
    if (!next || seen.has(next)) continue;
    seen.add(next);
    normalized.push(next);
  }
  return normalized;
}

/** Flattens API tag arrays, expanding any malformed PostgreSQL array literals. */
export function expandMediaAssetTags(tags: readonly string[]): string[] {
  return normalizeMediaTags(tags.flatMap((tag) => expandMediaAssetTag(String(tag))));
}
